import { cn } from "@/lib/utils";
import { ChevronDownIcon, CheckIcon } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

// Language display names and icons
const LANGUAGE_INFO: Record<string, { name: string; icon: string }> = {
  go: { name: "Go", icon: "Go" },
  python: { name: "Python", icon: "Py" },
  javascript: { name: "JavaScript", icon: "JS" },
  typescript: { name: "TypeScript", icon: "TS" },
  java: { name: "Java", icon: "Jv" },
  rust: { name: "Rust", icon: "Rs" },
  cpp: { name: "C++", icon: "C+" },
  c: { name: "C", icon: "C" },
  csharp: { name: "C#", icon: "C#" },
  ruby: { name: "Ruby", icon: "Rb" },
  swift: { name: "Swift", icon: "Sw" },
  kotlin: { name: "Kotlin", icon: "Kt" },
  sql: { name: "SQL", icon: "SQ" },
};

export function getLanguageInfo(lang: string) {
  return (
    LANGUAGE_INFO[lang] || { name: lang, icon: lang.slice(0, 2).toUpperCase() }
  );
}

interface LanguageSelectorProps {
  languages: string[];
  selected: string;
  onChange: (language: string) => void;
  disabled?: boolean;
}

export function LanguageSelector({
  languages,
  selected,
  onChange,
  disabled = false,
}: LanguageSelectorProps) {
  if (languages.length <= 1) {
    return null;
  }

  const selectedInfo = getLanguageInfo(selected);

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-muted-foreground">Language:</span>
      <DropdownMenu>
        <DropdownMenuTrigger asChild disabled={disabled}>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              "gap-2 min-w-[140px] justify-between",
              disabled && "opacity-50 cursor-not-allowed"
            )}
          >
            <span className="flex items-center gap-2">
              <span className="font-mono text-xs font-bold opacity-60">
                {selectedInfo.icon}
              </span>
              <span>{selectedInfo.name}</span>
            </span>
            <ChevronDownIcon className="size-4 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[180px]">
          <DropdownMenuRadioGroup value={selected} onValueChange={onChange}>
            {languages.map((lang) => {
              const info = getLanguageInfo(lang);
              return (
                <DropdownMenuRadioItem
                  key={lang}
                  value={lang}
                  className="gap-2 cursor-pointer"
                >
                  <span className="font-mono text-xs font-bold opacity-60 w-6">
                    {info.icon}
                  </span>
                  <span>{info.name}</span>
                </DropdownMenuRadioItem>
              );
            })}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
